import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Video, Square, FlipHorizontal, X, Check, Loader2, Camera as CameraIcon, Mic, MicOff, Music } from 'lucide-react';
import { toast } from 'sonner';
import MusicSelector from './MusicSelector';

// Available camera filters
const CAMERA_FILTERS = [
  { id: 'none', name: 'None', css: 'none' },
  { id: 'grayscale', name: 'B&W', css: 'grayscale(100%)' },
  { id: 'sepia', name: 'Sepia', css: 'sepia(100%)' },
  { id: 'vintage', name: 'Vintage', css: 'sepia(50%) contrast(90%) brightness(90%)' },
  { id: 'warm', name: 'Warm', css: 'sepia(30%) saturate(120%)' },
  { id: 'cool', name: 'Cool', css: 'hue-rotate(180deg) saturate(80%)' },
  { id: 'saturate', name: 'Vibrant', css: 'saturate(150%) contrast(110%)' },
  { id: 'contrast', name: 'Contrast', css: 'contrast(130%)' },
  { id: 'blur', name: 'Soft', css: 'blur(1px) brightness(105%)' },
  { id: 'brightness', name: 'Bright', css: 'brightness(120%)' },
  { id: 'invert', name: 'Invert', css: 'invert(100%)' },
  { id: 'hue-rotate', name: 'Psyche', css: 'hue-rotate(90deg) saturate(150%)' },
];

export default function CameraRecorder({ onRecordComplete, onCancel }) {
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const musicAudioRef = useRef(null);
  const initOnceRef = useRef(false);

  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [facingMode, setFacingMode] = useState('user');
  const [selectedFilter, setSelectedFilter] = useState(CAMERA_FILTERS[0]);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [showMusicSelector, setShowMusicSelector] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const durationIntervalRef = useRef(null);

  // Attach stream to video element
  const attachStream = (stream) => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.srcObject = stream;
      v.muted = true;
      v.playsInline = true;
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');
      v.setAttribute('muted', '');
      v.setAttribute('autoplay', '');
      // Best-effort play; ignore errors (some browsers throw on autoplay)
      const playPromise = v.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    } catch (e) {
      console.warn('attachStream error', e);
    }
  };

  // Initialize camera with progressive fallback
  const initCamera = useCallback(async (mode = facingMode) => {
    setIsLoading(true);
    setCameraError(null);

    // Stop any existing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera not supported on this browser');
      setIsLoading(false);
      return;
    }

    // Try a sequence of constraints (most specific → most permissive)
    const constraintList = [
      { video: { facingMode: { ideal: mode }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: !isMuted },
      { video: { facingMode: mode }, audio: !isMuted },
      { video: true, audio: !isMuted },
      { video: true, audio: false },
    ];

    let stream = null;
    let lastError = null;
    for (const c of constraintList) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(c);
        if (stream) break;
      } catch (e) {
        lastError = e;
        console.warn('getUserMedia failed for', c, e.name);
      }
    }

    if (!stream) {
      const err = lastError || new Error('Could not open camera');
      let msg = 'Could not open camera';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        msg = 'Camera permission denied. Please allow camera access in your browser settings.';
      } else if (err.name === 'NotFoundError') {
        msg = 'No camera found on this device';
      } else if (err.name === 'NotReadableError') {
        msg = 'Camera is in use by another app. Please close other apps and try again.';
      }
      setCameraError(msg);
      setIsLoading(false);
      return;
    }

    streamRef.current = stream;

    // Try to attach immediately; video element is always rendered now
    attachStream(stream);

    // Small grace period then mark loaded (video.onLoadedData also flips this)
    setTimeout(() => setIsLoading(false), 400);
  }, [facingMode, isMuted]);

  // Initial mount: init camera once
  useEffect(() => {
    if (initOnceRef.current) return;
    initOnceRef.current = true;
    initCamera('user');
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-attach stream whenever video element re-mounts and stream exists
  useEffect(() => {
    if (streamRef.current && videoRef.current && !recordedUrl) {
      attachStream(streamRef.current);
    }
  }, [recordedUrl, isLoading, cameraError]);

  const flipCamera = async () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newMode);
    await initCamera(newMode);
  };

  const toggleMute = () => {
    setIsMuted(prev => !prev);
    if (streamRef.current) {
      const audioTrack = streamRef.current.getAudioTracks()[0];
      if (audioTrack) audioTrack.enabled = isMuted;
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;

    chunksRef.current = [];

    // Pick a mimeType that the browser supports
    const candidates = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
    ];
    let mimeType = '';
    for (const m of candidates) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
        mimeType = m;
        break;
      }
    }

    try {
      const mediaRecorder = mimeType
        ? new MediaRecorder(streamRef.current, { mimeType })
        : new MediaRecorder(streamRef.current);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' });
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setRecordedUrl(url);

        if (musicAudioRef.current) {
          musicAudioRef.current.pause();
          musicAudioRef.current.currentTime = 0;
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setDuration(0);

      if (selectedMusic && musicAudioRef.current) {
        musicAudioRef.current.src = selectedMusic.preview_url;
        musicAudioRef.current.play().catch(() => {});
      }

      const MAX_DURATION = 180;
      durationIntervalRef.current = setInterval(() => {
        setDuration(prev => {
          if (prev >= MAX_DURATION - 1) {
            stopRecording();
            toast.info('3-minute limit reached!');
            return MAX_DURATION;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Could not start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      try { mediaRecorderRef.current.stop(); } catch {}
      setIsRecording(false);
      if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
      if (musicAudioRef.current) musicAudioRef.current.pause();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const handleConfirm = () => {
    if (recordedBlob) {
      const file = new File([recordedBlob], `video_${Date.now()}.webm`, { type: recordedBlob.type });
      onRecordComplete(file, recordedUrl, selectedFilter.css, selectedMusic);
    }
  };

  const handleRetake = () => {
    setRecordedBlob(null);
    setRecordedUrl(null);
    setDuration(0);
    initCamera(facingMode);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Preview recorded video
  if (recordedUrl) {
    return (
      <div className="fixed inset-0 bg-black z-[200] flex flex-col">
        <div className="flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent">
          <Button variant="ghost" size="icon" onClick={handleRetake} className="text-white" data-testid="retake-btn">
            <X className="w-6 h-6" />
          </Button>
          <span className="text-white font-medium">Preview</span>
          <Button variant="ghost" size="icon" onClick={handleConfirm} className="text-green-500" data-testid="confirm-recording-btn">
            <Check className="w-6 h-6" />
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center">
          <video
            src={recordedUrl}
            className="max-h-full max-w-full"
            style={{ filter: selectedFilter.css }}
            controls
            autoPlay
            playsInline
            loop
            data-testid="preview-video"
          />
        </div>

        <div className="p-4 bg-gradient-to-t from-black/80 to-transparent">
          <p className="text-white/70 text-sm mb-2">Apply Filter</p>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {CAMERA_FILTERS.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setSelectedFilter(filter)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm transition-colors ${
                  selectedFilter.id === filter.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-center gap-4 p-6 pb-10">
          <Button onClick={handleRetake} variant="outline" className="rounded-full px-8">Retake</Button>
          <Button onClick={handleConfirm} className="rounded-full px-8 bg-green-500 hover:bg-green-600">Use Video</Button>
        </div>
      </div>
    );
  }

  // Camera recording view — video element ALWAYS rendered to avoid ref race
  return (
    <div className="fixed inset-0 bg-black z-[200] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent absolute top-0 left-0 right-0 z-20">
        <Button variant="ghost" size="icon" onClick={onCancel} className="text-white" data-testid="close-camera-btn">
          <X className="w-6 h-6" />
        </Button>

        {isRecording && (
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-2 px-3 py-1 bg-red-500 rounded-full animate-pulse">
              <div className="w-2 h-2 bg-white rounded-full" />
              <span className="text-white text-sm font-medium">{formatDuration(duration)} / 3:00</span>
            </div>
            <div className="w-32 h-1 bg-white/30 rounded-full overflow-hidden">
              <div className="h-full bg-red-500 transition-all duration-1000" style={{ width: `${(duration / 180) * 100}%` }} />
            </div>
          </div>
        )}

        <div className="w-10" />
      </div>

      {/* Camera View — video always mounted */}
      <div className="flex-1 relative">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          webkit-playsinline="true"
          x-webkit-airplay="allow"
          disablePictureInPicture
          className="absolute inset-0 w-full h-full object-cover bg-black"
          style={{
            filter: selectedFilter.css,
            transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
          }}
          onLoadedMetadata={() => setIsLoading(false)}
          onCanPlay={() => setIsLoading(false)}
          onPlay={() => setIsLoading(false)}
          data-testid="camera-video"
        />

        {/* Loading overlay */}
        {isLoading && !cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 z-10">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
            <p className="text-white mt-3">Loading camera...</p>
          </div>
        )}

        {/* Error overlay */}
        {cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center p-8 bg-black/90 z-10">
            <CameraIcon className="w-16 h-16 text-white/50" />
            <p className="text-white/80">{cameraError}</p>
            <Button onClick={() => initCamera(facingMode)} variant="outline" data-testid="retry-camera-btn">
              Try Again
            </Button>
          </div>
        )}
      </div>

      {/* Filter Selection - only when not recording */}
      {!isRecording && !isLoading && !cameraError && (
        <div className="absolute bottom-40 left-0 right-0 px-4 z-10">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {CAMERA_FILTERS.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setSelectedFilter(filter)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm transition-colors ${
                  selectedFilter.id === filter.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-6 pb-10 bg-gradient-to-t from-black/80 to-transparent z-10">
        <div className="flex items-center justify-center gap-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={flipCamera}
            className="w-12 h-12 rounded-full bg-white/20 text-white hover:bg-white/30"
            disabled={isRecording}
            data-testid="flip-camera-btn"
          >
            <FlipHorizontal className="w-6 h-6" />
          </Button>

          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={`w-20 h-20 rounded-full border-4 border-white flex items-center justify-center transition-all ${
              isRecording ? 'bg-red-500' : 'bg-transparent hover:bg-white/10'
            }`}
            disabled={isLoading || !!cameraError}
            data-testid="record-toggle-btn"
          >
            {isRecording ? <Square className="w-8 h-8 text-white fill-white" /> : <div className="w-16 h-16 rounded-full bg-red-500" />}
          </button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowMusicSelector(true)}
            className={`w-12 h-12 rounded-full text-white ${
              selectedMusic ? 'bg-primary/50 hover:bg-primary/70' : 'bg-white/20 hover:bg-white/30'
            }`}
            disabled={isRecording}
            data-testid="music-btn"
          >
            <Music className="w-6 h-6" />
          </Button>
        </div>

        {selectedMusic && !isRecording && (
          <div className="flex items-center justify-center gap-2 mt-3 px-4 py-2 bg-white/10 rounded-full mx-auto w-fit">
            <Music className="w-4 h-4 text-primary" />
            <span className="text-white text-sm truncate max-w-[200px]">{selectedMusic.title}</span>
          </div>
        )}

        {!isRecording && !selectedMusic && !isLoading && !cameraError && (
          <p className="text-white/60 text-center text-sm mt-4">Tap the red button to start recording</p>
        )}
      </div>

      <audio ref={musicAudioRef} loop />

      {showMusicSelector && (
        <MusicSelector
          onSelect={(track) => { setSelectedMusic(track); setShowMusicSelector(false); }}
          onClose={() => setShowMusicSelector(false)}
          currentMusic={selectedMusic}
        />
      )}
    </div>
  );
}
